
/* product cards (snippets/product-card.liquid, heon-plp-card-metal.liquid; styles in heon-cards.css). Owned by the plp team.
   1. Photo row: a card prints its first photo plus up to four more (hidden); they go into one row (.hh-cg). Desktop: the
      photo follows the pointer. Touch: the row swipes, and with a keyboard the arrow keys move it.
   2. Metal dots: pick a metal to see it on the card (when the metal has its own photo) and to open or quick-shop it.
   Round 2 (2026-09-26): studio or lifestyle is read in idle time from a 12px copy decoded off the main thread
   (createImageBitmap), once per photo and remembered between pages; nothing is measured while cards are built.
   cards team (2026-09-26): photos placed by their own numbers (hh-f / hh-fc, snippets/heon-card-fit.liquid) skip that
   guess; each photo of the row sits in a slide of its own (.hh-cg-s) that clips it, and loads at its drawn width.
   cards3 team (2026-09-27; CEO: "the photos must change under the mouse and the finger on every product card, desktop
   and phone"): one photo row for every product card on the site, built here and styled in heon-cards.css:
   - the theme's cards (product-card.card--standard): collections, search, "Picked for you" under a product, 404, cart,
     ring size guide;
   - the homepage cards (.hh-card, sections/heon-home.liquid) and the journal's "Shop the story" cards (.hp-pcard,
     sections/heon-pages-article.liquid): their photo box carries data-hh-photos and holds the first photo plus up to
     four more (hidden, lazy).
   A card inside a row that scrolls sideways (homepage "Most loved" on phones, "Picked for you") swipes its photos too;
   a swipe that starts on the last photo (or back from the first) moves the row itself (heon-cards.css .hh-cg--rail).
   The arrow keys turn the photos of the card whose link has focus.
   3. Sold out on a 404 (cards3, 2026-09-27): Shopify draws a 404 for the visitor's own country, and outside the store's
      markets every piece reads unavailable. product-card.liquid then draws the card as available with data-hh-check;
      the product's own .js (a normal page, the site's market) says whether it really is sold out. */
(() => {
  const T = (k, d) => { const s = window.HeonI18n && window.HeonI18n[k]; return typeof s === 'string' && s ? s : d; };   // the page's language (snippets/heon-i18n-js), else the English default
  const TN = (k, n, d) => { let f = 'other'; try { f = new Intl.PluralRules(document.documentElement.lang || 'en').select(n); } catch (e) {} return T(k + (f === 'one' ? '_one' : '_other'), d).replace('[n]', n); };
  const MAX = 5;
  const RAIL = '.hr-track, .hh-grid, carousel-slider, slider-component, .slider';
  const REDUCE = matchMedia('(prefers-reduced-motion:reduce)');
  const fine = matchMedia('(hover:hover) and (pointer:fine)');
  const key = img => (img.getAttribute('src') || '').split('?')[0];
  const placed = img => img.classList.contains('hh-f') || img.classList.contains('hh-fc');
  const scale = img => Math.max(1, parseFloat(img.style.getPropertyValue('--s')) || 1);
  // speed (tema team, 2026-09-29, PSI phone): the width comes from the observer's own entry, so waking a screenful of
  // cards measures nothing; and touch screens start watching only after the page has loaded, so the extra photos
  // never compete with the first screen (a finger on a card wakes it at once)
  const near = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => {
    if (e.isIntersecting) { near.unobserve(e.target); wake(e.target, e.boundingClientRect.width); }
  }), { rootMargin: '300px 0px' }) : null;
  const loaded = new Promise(r => { if (document.readyState === 'complete') r(); else addEventListener('load', r, { once: true }); });

  // extra photos stay hidden (so the browser skips them) until the card is hovered or near the screen on touch; then
  // they load at once (cards3: a lazy photo off to the side in a row that scrolls would wait until swiped into view)
  function wake(cg, w0) {
    if (cg.dataset.awake) return; cg.dataset.awake = '1';
    const w = Math.ceil(w0 || cg.clientWidth) || 300;
    cg.querySelectorAll('img').forEach((img, k) => { if (k) { img.sizes = Math.ceil(w * scale(img)) + 'px'; img.loading = 'eager'; img.hidden = false; } });
  }
  function show(cg, i) {
    if (cg._i === i) return; cg._i = i;
    cg.querySelectorAll('img').forEach((img, k) => img.classList.toggle('is-on', k === i));
    if (cg.scrollWidth <= cg.clientWidth + 1) mark(cg, i);  // a row that scrolls moves the line from its scroll event
  }
  // the line under the photo (dots team, 2026-09-27): its dark segment stands under photo p (0 = the first; a fraction
  // while a finger swipes, so it moves with the finger)
  function mark(cg, p) { cg._mark.style.transform = 'translateX(' + p * 100 + '%)'; }

  // ---- studio or lifestyle ----
  // cards are 4:5 (cards.css): studio shots and size cards stay whole at the top of the frame, model and lifestyle
  // photos fill it. A photo counts as studio when most of its outer edge is near white.
  const STORE = 'hh-cards-photo';
  let known = {};
  try { known = JSON.parse(localStorage.getItem(STORE) || '{}') || {}; } catch (e) { known = {}; }
  let saveT = 0;
  const save = () => { clearTimeout(saveT); saveT = setTimeout(() => {
    try { const ks = Object.keys(known); if (ks.length > 800) ks.slice(0, ks.length - 800).forEach(k => delete known[k]); localStorage.setItem(STORE, JSON.stringify(known)); } catch (e) {}
  }, 1500); };
  const idle = window.requestIdleCallback ? cb => requestIdleCallback(cb, { timeout: 1200 }) : cb => setTimeout(cb, 80);
  let probe = null, pc = null;
  const queue = []; let busy = false;
  function classify(img) {
    const k = key(img);
    if (k in known) { img.classList.toggle('is-photo', !!known[k]); return; }
    const go = () => { queue.push(img); pump(); };
    img.complete && img.naturalWidth ? go() : img.addEventListener('load', go, { once: true });
  }
  function pump() {
    if (busy || !queue.length) return;
    busy = true;
    idle(async () => {
      const img = queue.shift(), k = key(img);
      try {
        if (k in known) img.classList.toggle('is-photo', !!known[k]);
        else {
          if (!probe) { probe = document.createElement('canvas'); probe.width = probe.height = 12; pc = probe.getContext('2d', { willReadFrequently: true }); }
          let src = img;
          if (window.createImageBitmap) {
            try { src = await createImageBitmap(img, { resizeWidth: 12, resizeHeight: 12, resizeQuality: 'low' }); } catch (e) { src = img; }
          }
          pc.clearRect(0, 0, 12, 12); pc.drawImage(src, 0, 0, 12, 12);
          if (src !== img && src.close) src.close();
          const d = pc.getImageData(0, 0, 12, 12).data;
          let light = 0, n = 0;
          for (let y = 0; y < 12; y++) for (let x = 0; x < 12; x++) {
            if (x > 0 && x < 11 && y > 0 && y < 11) continue;
            const q = (y * 12 + x) * 4; n++;
            if (Math.min(d[q], d[q + 1], d[q + 2]) > 220) light++;
          }
          known[k] = light / n < .47 ? 1 : 0; save();
          img.classList.toggle('is-photo', !!known[k]);
        }
      } catch (e) { /* a photo from another domain can't be read: it stays whole */ }
      busy = false; pump();
    });
  }

  function build(card) {
    card.dataset.hhCg = '';
    const media = card.querySelector('.card__media'), link = media && media.querySelector('a.media');
    if (!link || card.classList.contains('card--related')) return;
    const hover = link.querySelector('.card__hover-image'), seen = new Set(), pics = [];
    [...link.querySelectorAll('.card__main-image'), hover].forEach(img => {
      if (!img || pics.length >= MAX || seen.has(key(img))) return;
      seen.add(key(img)); pics.push(img);
    });
    pics.forEach(img => { if (!placed(img)) classify(img); });
    if (pics.length < 2) return;
    if (hover && !pics.includes(hover)) hover.remove();
    media.classList.remove('has-hover-image');  // the theme's own hover swap is replaced by this
    pics.forEach(img => img.classList.remove('card__hover-image', 'is-inactive'));
    // (the name carries the comma read out before its subtitle, polish 2026-09-26: drop it here)
    const name = ((card.querySelector('.hh-cn') || {}).textContent || '').trim().replace(/,$/, '');
    row(card, link, pics, link, name);
  }
  // the homepage and journal cards (cards3 team): a box marked data-hh-photos holds the first photo and up to four more
  function build2(box) {
    box.dataset.hhCg = '';
    more(box);
    const pics = [...box.children].filter(el => el.tagName === 'IMG').slice(0, MAX);
    if (pics.length < 2) return;
    const card = box.closest('[data-hh-card]') || box.parentElement, link = box.closest('a[href]') || card.querySelector('a[href]');
    row(card, box, pics, link, null);
  }

  // speed (tema team, 2026-09-29): the homepage prints its cards' extra photos as data (data-hh-more: "url|class|style|
  // sizes" joined by "^", url at width 640) instead of 96 hidden <img>; they become the same hidden lazy photos here
  function more(box) {
    const d = box.dataset.hhMore; if (!d) return;
    box.removeAttribute('data-hh-more');
    d.split('^').forEach(s => {
      const [u, c, st, sz] = s.split('|'); if (!u) return;
      const img = document.createElement('img');
      img.className = 'hh-x' + (c ? ' ' + c : ''); if (st) img.setAttribute('style', st);
      img.alt = (box.querySelector('img') || {}).alt || ''; img.loading = 'lazy'; img.hidden = true; if (sz) img.sizes = sz;
      img.srcset = [400, 640, 900].map(n => u.replace(/([?&]width=)\d+/, '$1' + n) + ' ' + n + 'w').join(', ');
      img.src = u;
      box.appendChild(img);
    });
  }

  // the photo row itself, the same for every card
  // name: the card's photo box is its own stop for the keyboard on touch screens (the theme's cards, whose photo link is
  // out of the tab order); null: the card's link already is that stop (homepage and journal cards are one link)
  function row(card, box, pics, link, name) {
    const cg = document.createElement('span'), bar = document.createElement('span'), n = pics.length;
    cg.className = 'hh-cg'; bar.className = 'hh-cg-bar'; bar.setAttribute('aria-hidden', 'true');
    pics.forEach((img, k) => {
      if (k) img.hidden = true;
      const sl = document.createElement('span'); sl.className = 'hh-cg-s'; sl.appendChild(img);
      cg.appendChild(sl);
    });
    // dots team (2026-09-27, CEO: the bars on the photo seemed to glow): one hairline under the photo, outside it, with a
    // dark segment one photo wide
    cg._mark = bar.appendChild(document.createElement('i'));
    bar.style.setProperty('--n', n);
    // the first photo shows, without asking the page for its size (speed, 2026-09-29: show() measured every card)
    box.prepend(cg); box.after(bar); pics[0].classList.add('is-on'); cg._i = 0;
    // go to a photo: a row that swipes scrolls to it, the others switch at once (keyboard, metal dots)
    cg.hhGo = (i, smooth) => {
      i = Math.max(0, Math.min(n - 1, i));
      if (cg.scrollWidth > cg.clientWidth + 1) cg.scrollTo({ left: i * cg.clientWidth, behavior: smooth && !REDUCE.matches ? 'smooth' : 'auto' });
      show(cg, i);
    };
    // touch: the photos swipe (a Swiper carousel moves its slides by script and keeps the swipe to itself). In a row that
    // scrolls sideways the swipe hands over to the row at the last photo and back at the first (.hh-cg--rail)
    if (!card.closest('.swiper, product-swiper')) {
      cg.classList.add('hh-cg--swipe'); bar.classList.add('hh-cg-bar--swipe');
      if (card.closest(RAIL)) cg.classList.add('hh-cg--rail');
      let raf = 0;
      cg.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0; const p = Math.max(0, Math.min(n - 1, cg.scrollLeft / (cg.clientWidth || 1)));
          show(cg, Math.round(p)); mark(cg, p);
        });
      }, { passive: true });
      if (!fine.matches) {
        near ? loaded.then(() => idle(() => near.observe(cg))) : wake(cg);
        cg.addEventListener('touchstart', () => wake(cg), { passive: true, once: true });
        // the row scrolls, so a keyboard can reach it: Tab stops on it, the arrow keys turn the photos, Enter opens the piece
        if (name !== null) {
          cg.tabIndex = 0; cg.setAttribute('role', 'group');
          cg.setAttribute('aria-label', (name ? name + ', ' : '') + TN('photos', n, '[n] photos'));
          cg.addEventListener('keydown', e => { if (e.key === 'Enter' && link) { e.preventDefault(); link.click(); } });
        }
      }
    }
    // keyboard: the arrow keys turn the photos while the card's link (or its photo row) has focus; the metal dots keep
    // their own arrow keys
    card.addEventListener('keydown', e => {
      const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      if (!step || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.target !== cg && !(e.target.matches && e.target.matches('a[href]'))) return;
      e.preventDefault(); wake(cg); cg.hhGo((cg._i || 0) + step, true);
    });
    // desktop: the photo under the pointer; the name and Quick shop below keep the photo it last showed. Not while a
    // button is held (dragging a row with the mouse)
    card.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') wake(cg); });
    card.addEventListener('pointermove', e => {
      if (e.pointerType !== 'mouse' || !fine.matches || e.buttons) return;
      const r = cg.getBoundingClientRect();
      if (e.clientY < r.top || e.clientY > r.bottom) return;
      show(cg, Math.max(0, Math.min(n - 1, Math.floor((e.clientX - r.left) / r.width * n))));
    });
    card.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') show(cg, 0); });
    return cg;
  }

  // ---- metal dots (snippets/heon-plp-card-metal.liquid) ----
  // A dot whose metal has its own photo is a radio button. Hovering it with a mouse previews that metal until the
  // pointer leaves the card; a click, a tap or the arrow keys keep it. The card then shows the metal's photo (over the
  // gallery, which rests until the metal in the first photo is picked again), says "14K rose gold" and its links and
  // Quick shop open that variant. While the photo loads, the card shows a thin moving line (hh-metal-wait).
  // Dots of pieces without photos per metal are plain links to the piece with that metal chosen: no script.
  // a dot without a variant (rings: the product page asks for a size) leaves the links as printed
  const withVariant = (url, id) => {
    if (!id) return url;
    const u = new URL(url, location.href); u.searchParams.set('variant', id);
    return u.origin === location.origin ? u.pathname + u.search + u.hash : u.href;
  };
  function metal(card) {
    const box = card.querySelector('.hh-cm-metal');
    if (!box || box.dataset.ready) return;
    box.dataset.ready = '1';
    const dots = [...box.querySelectorAll('button[role="radio"]')];
    if (!dots.length) return;
    const label = box.querySelector('.hh-cm-label'), base = label.textContent;
    const link = card.querySelector('.card__media a.media');
    const links = [...card.querySelectorAll('a.js-prod-link')];
    const quick = [...card.querySelectorAll('.js-quick-add[data-product-url]')];
    links.forEach(a => { a.dataset.hhHref = a.getAttribute('href'); });
    quick.forEach(b => { b.dataset.hhHref = b.dataset.productUrl; });
    const first = dots.find(d => d.getAttribute('aria-checked') === 'true') || null;  // the metal the card opens with
    let kept = null, cur = null, timer = 0, mv = null;
    const pics = new Map();

    function photo(dot) {
      if (!link || !dot.dataset.src) return null;
      let img = pics.get(dot);
      if (img) return img;
      if (!mv) {
        mv = document.createElement('span'); mv.className = 'hh-mv'; mv.setAttribute('aria-hidden', 'true');
        link.append(mv);
      }
      img = new Image(); img.alt = ''; img.decoding = 'async';
      img.className = 'img-fit img-fit--contain';
      const fit = (dot.dataset.fit || '').split('|');
      if (fit[0]) img.classList.add(fit[0]);
      if (fit[1]) img.setAttribute('style', fit[1]);
      img.sizes = Math.ceil((link.clientWidth || 300) * scale(img)) + 'px';
      img.srcset = dot.dataset.srcset; img.src = dot.dataset.src;
      if (!img.complete) {
        img.addEventListener('load', () => { if (cur === dot || (!cur && kept === dot)) paint(); }, { once: true });
        img.addEventListener('error', () => { pics.delete(dot); delete dot.dataset.src; if (cur === dot || (!cur && kept === dot)) paint(); }, { once: true });
      }
      if (!placed(img)) classify(img);
      mv.append(img); pics.set(dot, img);
      return img;
    }
    // what the card shows: cur (a preview), else kept (a choice), else the card as printed
    function paint() {
      const dot = cur || kept;
      dots.forEach(d => d.classList.toggle('is-shown', d === (dot || first)));
      label.textContent = dot ? dot.dataset.name : base;
      links.forEach(a => { a.href = dot ? withVariant(a.dataset.hhHref, dot.dataset.variant) : a.dataset.hhHref; });
      quick.forEach(b => { b.dataset.productUrl = dot ? withVariant(b.dataset.hhHref, dot.dataset.variant) : b.dataset.hhHref; });
      const img = dot && dot !== first ? photo(dot) : null;
      const ready = img && img.complete && img.naturalWidth;
      if (link) link.classList.toggle('hh-metal-wait', !!(img && !ready));
      if (mv) {
        // the photo swaps once it has loaded, so the card never flashes empty
        if (img && !ready) return;
        mv.classList.toggle('is-on', !!img); link.classList.toggle('hh-metal-on', !!img);
        pics.forEach(p => p.classList.toggle('is-on', p === img));
      }
      // back to the first photo (a row that swipes scrolls back to it)
      if (!img) { const cg = link && link.querySelector('.hh-cg'); if (cg) cg.hhGo ? cg.hhGo(0) : show(cg, 0); }
    }
    function keep(dot) {
      kept = dot; cur = null;
      dots.forEach(d => { const on = d === dot; d.setAttribute('aria-checked', on); d.tabIndex = on ? 0 : -1; });
      paint();
    }
    if (!first) dots[0].tabIndex = 0;
    dots.forEach((dot, k) => {
      dot.addEventListener('pointerenter', e => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(timer);
        timer = setTimeout(() => { cur = dot; paint(); }, 60);  // a pointer sweeping past doesn't count
      });
      dot.addEventListener('pointerleave', () => clearTimeout(timer));
      dot.addEventListener('click', e => { e.preventDefault(); keep(dot); });
      dot.addEventListener('keydown', e => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        const next = dots[(k + step + dots.length) % dots.length];
        next.focus(); keep(next);
      });
    });
    // desktop: load the other metals' photos as soon as the pointer comes near, so the swap is instant
    card.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') dots.forEach(d => { if (d !== first) photo(d); }); });
    card.addEventListener('pointerleave', e => {
      if (e.pointerType !== 'mouse') return;
      clearTimeout(timer);
      if (cur) { cur = null; paint(); }
    });
  }

  // 3. a card drawn on a 404 outside the store's markets: show "Sold out" only if the product says so on a normal page
  function check(card) {
    const url = (card.dataset.hhCheck || '').split('?')[0]; card.removeAttribute('data-hh-check');
    if (!url) return;
    fetch(url + '.js', { headers: { Accept: 'application/json' } }).then(r => r.ok ? r.json() : null).then(p => {
      if (!p || p.available !== false) return;
      const label = card.querySelector('[data-hh-so]'), strings = window.theme && theme.strings;
      const text = (label && label.textContent.trim()) || (strings && strings.noStock) || T('sold_out', 'Sold out');
      if (label) label.hidden = false;
      card.querySelectorAll('.js-quick-add, .quick-add-btn').forEach(b => {
        b.disabled = true;
        b.querySelectorAll('.hh-qa-short, .visually-hidden').forEach(e => e.remove());
        const t = b.querySelector('.quick-add-btn-text'); if (t) { t.classList.remove('hh-qa-long'); t.textContent = text; }
      });
    }).catch(() => {});
  }

  // speed (tema team, 2026-09-29; PSI phone: one 230 ms task on the homepage): the cards are built in slices of about
  // 8 ms, top of the page first, each card its photo row and then its dots; a card not built yet shows its first photo
  const queue2 = []; let running = false;
  function run() {
    const t0 = performance.now();
    while (queue2.length && performance.now() - t0 < 8) {
      const el = queue2.shift();
      if (el.hasAttribute('data-hh-cg')) continue;
      if (el.tagName === 'PRODUCT-CARD') { build(el); metal(el); } else build2(el);
    }
    if (queue2.length) setTimeout(run, 0); else running = false;
  }
  const scan = () => {
    document.querySelectorAll('product-card.card--standard:not([data-hh-cg]), [data-hh-photos]:not([data-hh-cg])').forEach(el => {
      if (!el.hhQueued) { el.hhQueued = 1; queue2.push(el); }
    });
    document.querySelectorAll('product-card[data-hh-check]').forEach(check);
    if (!running && queue2.length) { running = true; run(); }
  };
  scan();
  // filters, sorting and "load more" swap the grid in place
  let pending = 0;
  new MutationObserver(() => { if (!pending) pending = requestAnimationFrame(() => { pending = 0; scan(); }); })
    .observe(document.body, { childList: true, subtree: true });
})();
